// Resin volume + mix-ratio calculator
class ResinCalculator extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('input', this.#handleInput, { signal });
    this.#calculate();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handleInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'shape') {
      this.#setShape(event.target.value);
      return;
    }

    this.#calculate();
  };

  /**
   * @param {string} shape
   */
  #setShape(shape) {
    for (const panel of this.querySelectorAll('[data-shape-panel]')) {
      panel.hidden = panel.getAttribute('data-shape-panel') !== shape;
    }

    this.#calculate();
  }

  /**
   * @param {string} name
   * @returns {HTMLInputElement | HTMLSelectElement | null}
   */
  #field(name) {
    return this.querySelector(`[data-field="${name}"]`);
  }

  /**
   * @param {string} name
   * @returns {number}
   */
  #value(name) {
    const field = this.#field(name);

    return field ? parseFloat(field.value) || 0 : 0;
  }

  #calculate() {
    const checkedShape = this.querySelector('input[name="shape"]:checked');
    const activeShape = checkedShape instanceof HTMLInputElement ? checkedShape.value : 'rectangle';

    let cubicInches = 0;

    if (activeShape === 'circle') {
      const radius = this.#value('diameter') / 2;
      cubicInches = Math.PI * radius * radius * this.#value('thickness-circle');
    } else {
      cubicInches = this.#value('length') * this.#value('width') * this.#value('thickness-rect');
    }

    const cubicFeetResult = cubicInches / 1728;
    const flOz = cubicInches / 1.805;
    const gallons = cubicInches / 231;
    const mL = cubicInches * 16.3871;
    const liters = cubicInches / 61.024;

    this.#setResult('cubic-inches', cubicInches);
    this.#setResult('cubic-feet', cubicFeetResult);
    this.#setResult('floz', flOz);
    this.#setResult('gal', gallons);
    this.#setResult('ml', mL);
    this.#setResult('l', liters);

    this.#calculateMix(flOz, mL);
  }

  /**
   * @param {number} flOz
   * @param {number} mL
   */
  #calculateMix(flOz, mL) {
    const mixResult = this.querySelector('[data-result-mix]');

    if (!mixResult) return;

    let ratioA = parseFloat(this.dataset.ratioA || '') || 0;
    let ratioB = parseFloat(this.dataset.ratioB || '') || 0;

    const productSelect = this.#field('product');

    if (productSelect instanceof HTMLSelectElement) {
      const selected = productSelect.selectedOptions[0];

      ratioA = selected ? parseFloat(selected.dataset.ratioA || '') || 0 : 0;
      ratioB = selected ? parseFloat(selected.dataset.ratioB || '') || 0 : 0;
    }

    if (ratioA <= 0 || ratioB <= 0) {
      mixResult.hidden = true;
      return;
    }

    mixResult.hidden = false;

    const totalParts = ratioA + ratioB;

    this.#setResult('part-a-floz', flOz * (ratioA / totalParts));
    this.#setResult('part-b-floz', flOz * (ratioB / totalParts));
    this.#setResult('part-a-ml', mL * (ratioA / totalParts));
    this.#setResult('part-b-ml', mL * (ratioB / totalParts));
  }

  /**
   * @param {string} name
   * @param {number} value
   */
  #setResult(name, value) {
    const el = this.querySelector(`[data-result="${name}"]`);

    if (el) el.textContent = value.toFixed(2);
  }
}

if (!customElements.get('resin-calculator')) {
  customElements.define('resin-calculator', ResinCalculator);
}
